import { useCallback, useState, type MouseEvent, type ReactNode } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useSelector } from 'react-redux';
import HistoricalTrendChartIcon from '@/components/icons/historicalTrendChart';
import TableChartIcon from '@/components/icons/tableChart';
import { ViewMode } from '@/constants/enum';
import { useLocalization } from '@/hooks/useLocalization';
import type { WARootState } from '@/store/store';
import { safeGetLocalStorage } from '@/utils/safeStorage';
import { userScopeId, userScopedKey } from '@/utils/userScopedSessionKey';

export const HISTORICAL_TREND_VIEW_KEY = 'historicalTrendView';

type TrendViewMode = ViewMode.Chart | ViewMode.Table;

function readTrendViewMode(storageKey: string): TrendViewMode {
  try {
    const saved = safeGetLocalStorage()?.getItem(storageKey);
    return saved === ViewMode.Table ? ViewMode.Table : ViewMode.Chart;
  } catch {
    return ViewMode.Chart;
  }
}

function writeTrendViewMode(storageKey: string, mode: TrendViewMode): void {
  try {
    safeGetLocalStorage()?.setItem(storageKey, mode);
  } catch {
    return;
  }
}

interface HistoricalTrendViewProps {
  chart: ReactNode;
  table: ReactNode;
}

export default function HistoricalTrendView({
  chart,
  table,
}: HistoricalTrendViewProps) {
  const { t } = useLocalization();
  const user = useSelector((state: WARootState) => state.auth.user);
  const role = useSelector((state: WARootState) => state.auth.currentRole);
  const storageKey = userScopedKey(
    HISTORICAL_TREND_VIEW_KEY,
    userScopeId(user, role),
  );

  const [selection, setSelection] = useState(() => ({
    storageKey,
    mode: readTrendViewMode(storageKey),
  }));
  const mode =
    selection.storageKey === storageKey
      ? selection.mode
      : readTrendViewMode(storageKey);

  const handleViewModeChange = useCallback(
    (_event: MouseEvent<HTMLElement>, next: TrendViewMode | null) => {
      if (next === null) return;
      writeTrendViewMode(storageKey, next);
      setSelection({ storageKey, mode: next });
    },
    [storageKey],
  );

  return (
    <Stack gap={2}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          displayPrint: 'none',
        }}
      >
        <ToggleButtonGroup
          value={mode}
          exclusive
          size='small'
          onChange={handleViewModeChange}
          aria-label={t('historicalTrend.viewToggle', 'Chart or table')}
          data-testid='historical-trend-toggle'
        >
          <ToggleButton
            value={ViewMode.Chart}
            aria-label={t('historicalTrend.chart', 'Chart')}
          >
            <HistoricalTrendChartIcon
              sx={{ color: (theme) => theme.palette.text.primary }}
            />
          </ToggleButton>
          <ToggleButton
            value={ViewMode.Table}
            aria-label={t('historicalTrend.table', 'Table')}
          >
            <TableChartIcon
              sx={{ color: (theme) => theme.palette.text.primary }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {mode === ViewMode.Chart ? chart : table}
    </Stack>
  );
}
